import useURLParams from 'hooks/useURLParams'
import { Section } from '../components/Section/Section'
import { useDemographics } from 'hooks/queries/useDemographics/useDemographics'
import { WorldMap } from '../components/WorldMap/WorldMap'
import Styles from '../Analytics.module.scss'
import { Distribution, DistributionMetric } from '../components/DistributionMetrics/DistributionMetrics'
import { CountryInfo, countryCodeMapping } from '../components/DistributionMetrics/countryCodeMapping'
import { useAnalyticsContext } from '../AnalyticsContext'

interface GeoDistribution {
  code: string
  percentage: number
}

const deriveLanguageDistribution = (
  countryData: GeoDistribution[],
  countryMapping: Record<string, CountryInfo>,
): Distribution[] => {
  const languagePercentages = new Map<string, number>()

  countryData.forEach((country) => {
    const countryInfo = countryMapping[country.code]
    if (countryInfo?.officialLanguages) {
      const percentagePerLanguage = country.percentage / countryInfo.officialLanguages.length

      countryInfo.officialLanguages.forEach((lang) => {
        const currentPercentage = languagePercentages.get(lang) || 0
        languagePercentages.set(lang, currentPercentage + percentagePerLanguage)
      })
    }
  })

  return Array.from(languagePercentages.entries())
    .map(([language, percentage]) => ({
      label: language,
      percentage: Number(percentage.toFixed(2)),
    }))
    .sort((a, b) => b.percentage - a.percentage)
    .slice(0, 5)
}

export const LocationAndLanguage = () => {
  const [searchParams] = useURLParams()
  const integrationId = parseInt(searchParams.get('external_integration_id') as string)
  const { period } = useAnalyticsContext()
  const { data, isFetching } = useDemographics(integrationId, ['country', 'city'], period)

  const countryData =
    data?.country?.geographic_distribution.map((item) => ({
      label: countryCodeMapping[item.code]?.name || item.code,
      code: item.code,
      percentage: item.percentage,
    })) || []

  const cityData =
    data?.city?.top_cities.map((item) => ({
      label: item.name,
      percentage: item.percentage,
    })) || []

  const languageData = deriveLanguageDistribution(data?.country?.geographic_distribution || [], countryCodeMapping)

  const countryError = {
    title: data?.country?.error_title,
    message: data?.country?.error_message,
  }
  const cityError = {
    title: data?.city?.error_title,
    message: data?.city?.error_message,
  }
  const languageError = {
    title: data?.country?.error_title,
    message: data?.country?.error_message,
  }

  return (
    <Section title="Location and Language">
      <WorldMap data={data?.country} isFetching={isFetching} />
      <div className={Styles.metricsGroup}>
        <DistributionMetric
          title="Top Countries"
          tooltip="Distribution of audience by country"
          data={countryData}
          isFetching={isFetching}
          errorTitle={countryError.title}
          errorMessage={countryError.message}
        />
        <DistributionMetric
          title="Top Cities"
          tooltip="Distribution of audience by city"
          data={cityData}
          isFetching={isFetching}
          errorTitle={cityError.title}
          errorMessage={cityError.message}
        />
        <DistributionMetric
          title="Top Language"
          tooltip="Distribution of audience by language"
          data={languageData}
          isFetching={isFetching}
          errorTitle={languageError.title}
          errorMessage={languageError.message}
        />
      </div>
    </Section>
  )
}
